import _ from 'lodash';
import React, { Suspense, useEffect, useState } from 'react';
import { Panel } from 'react-bootstrap';
import { useDispatch } from 'react-redux';

import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';

import { useAlertStore } from '../../store/providers/Alert';
import { useDepartmentOfRealEstateStore } from '../../store/providers/DepartmentOfRealEstate';

import * as constants from '../../constants';

import DepartmentOfRealEstateModal from '../../components/Modals/DepartmentOfRealEstateModal/DepartmentOfRealEstateModal';
import Spinner from '../../components/Spinner/Spinner';
import { columns, sorting } from './DepartmentOfRealEstateListSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));

function DepartmentOfRealEstate() {
    const pagePermission = constants.PERMISSION_SETTINGS_DEPARTMENTOFREALESTATE;
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { selectRecord, listRecords, newRecord, deleteRecord, data, clearData, getAvailableStates } = useDepartmentOfRealEstateStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [listData, setListData] = useState([]);
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [availableStates, setAvailableStates] = useState([]);

    const selectedHandler = (departmentOfRealEstate) => {
        setIsEditing(true);
        selectRecord({ departmentOfRealEstateId: departmentOfRealEstate.id });
    };

    const addedHandler = () => {
        setIsEditing(true);
        newRecord();
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        listRecords();
        getAvailableStates();
    };

    const deletedHandler = (departmentOfRealEstate) => {
        deleteRecord({ departmentOfRealEstateId: departmentOfRealEstate.id });
    };

    const refreshedHandler = () => {
        listRecords();
    };

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
                getAvailableStates();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            if (data.status === 'success' && data.scope === 'availableStates') {
                setAvailableStates(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords, getAvailableStates]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'departmentOfRealEstate', parent: 'Settings', text: 'Department of Real Estate', path: window.location.pathname }));
        listRecords();
        getAvailableStates();

        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, getAvailableStates, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    return (
        <>
            <SideMenu activeKey="settings" />
            <div className="content-body">
                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit} />
                {hasPermission && isMounted &&
                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            isEditing && <DepartmentOfRealEstateModal
                                show={isEditing}
                                canEdit={canEdit}
                                title={(!_.isEmpty(data) && !_.isEmpty(data.body)) ? 'Edit Department of Real Estate' : 'Add Department of Real Estate'}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()}
                                availableStates={availableStates} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Department of Real Estate" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Department of Real Estate"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        permissionEdit={canEdit}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        added={() => addedHandler()}
                                        selected={(departmentOfRealEstate) => selectedHandler(departmentOfRealEstate)}
                                        deleted={(departmentOfRealEstate) => deletedHandler(departmentOfRealEstate)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(DepartmentOfRealEstate));